import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { isNil } from 'lodash'
import type { ReactNode } from 'react'

import { cn } from '@/legacy-src/lib/utils'

import { Text, type TextProps } from '@/src/domains/design-system/components/Text'

const tagVariants = cva('inline-block rounded-lg text-petrol-500', {
  variants: {
    size: {
      xs: 'p-1',
      s: 'p-1',
      m: 'p-2',
      l: 'rounded-xl p-2',
    },
    active: {
      true: 'appearance-none ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:border-sand-400 disabled:bg-sand-400 disabled:!text-sand-700',
      false: '',
    },
    variant: {
      blue: 'bg-blue-500',
      sand: 'bg-sand-400',
      yellow: 'bg-yellow-500 text-petrol-400',
      strikethrough: 'bg-petrol-400 text-white line-through',
      white: 'bg-white',
      outline: 'border-2 border-solid border-petrol-500 bg-transparent',
      petrol: 'bg-petrol-500 text-white-500',
    },
  },
  compoundVariants: [
    {
      variant: 'outline',
      size: 'xs',
      className: 'p-[2px]',
    },
    {
      variant: 'outline',
      size: 's',
      className: 'p-[2px]',
    },
    {
      variant: 'outline',
      size: 'm',
      className: 'p-[6px]',
    },
    {
      variant: 'outline',
      size: 'l',
      className: 'p-[6px]',
    },
    {
      variant: 'blue',
      active: true,
      className: 'border-none hover:bg-blue-600',
    },
    {
      variant: 'sand',
      active: true,
      className: 'border-none hover:bg-sand-600',
    },
    {
      variant: 'yellow',
      active: true,
      className: 'border-none hover:bg-yellow-600',
    },
    {
      variant: 'strikethrough',
      active: true,
      className: 'border-none hover:bg-petrol-600',
    },
    {
      variant: 'white',
      active: true,
      className: 'border-none hover:bg-sand-600',
    },
    {
      variant: 'outline',
      active: true,
      className: 'hover:border-petrol-300 hover:text-petrol-300',
    },
  ],
})

type TagVariantsProps = VariantProps<typeof tagVariants>

export interface TagProps extends TagVariantsProps {
  size: Exclude<TagVariantsProps['size'], null | undefined>
  variant?: TagVariantsProps['variant']
  active?: TagVariantsProps['active']
  asChild?: boolean
  children: ReactNode
  className?: string
}

const textSizeMap: Record<NonNullable<TagProps['size']>, TextProps['variant']> = {
  xs: 'xs',
  s: 'sm',
  m: 'sm',
  l: 'base',
}

export const Tag = ({ size, variant, active, asChild, ...rest }: TagProps) => {
  const className = tagVariants({ size, variant, active })

  const Comp = asChild ? Slot : 'span'

  return (
    <Text
      variant={isNil(size) ? undefined : textSizeMap[size]}
      weight={variant === 'strikethrough' ? 'regular' : 'bold'}
      asChild
      className={cn(className, rest.className)}
    >
      <Comp>{rest.children}</Comp>
    </Text>
  )
}
